import React, { Component } from 'react';
import TabSection, { SectionPlaceholder } from './TabSection';
import ProfileDateBlock from './ProfileDateBlock';
import moment from "moment";
import * as Cookies from "es-cookie";
import { Row, Col, Modal } from "react-bootstrap";
import { components } from "react-select";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import SelectDropDown from '../selectdropdown/SelectDropDown';
import CardLessDataTableWithSearch from '../datatables/CardLessDataTableWithSearch';
import { PageShimmer } from './Shimmer';
import { pointTooltip } from './chartTooltip';

// College bridge: viewed student comes from props; resolve mobile from it, and
// replace "You/Your" with the student's (first) name.
const resolveMobile = (student) =>
    (student && (student.contact_no || student.student_mobile || student.mobile)) || null;
const studentName = (student) => {
    const full = (student && (student.student_name || student.name)) || "";
    return full.trim() ? full.trim() : "Student";
};
const studentFirstName = (student) => studentName(student).split(/\s+/)[0];

// Custom dropdown indicator (chevron) for the exam SelectDropDown.
const DropdownIndicator = (props) =>
    components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
            <i className="fal fa-angle-down" />
        </components.DropdownIndicator>
    );

const examTypes = [
    { value: 0, label: "All Exams" },
    { value: 1, label: "Mock Test" },
    { value: 2, label: "Custom Exam" },
    { value: 3, label: "Scheduled Exam" },
    { value: 4, label: "Error Exam" },
    { value: 6, label: "Practice Test" },
    { value: 7, label: "Adaptive Test" },
];


const GET_STUDENT_PERFORMANCE_REPORT = gql`
  query (
  $mobile: String
    $student_user_id: Int
    $edu_student_id: Int
    $exam_type: Int
    $exam_stream: Int
    $fromDate: String
    $toDate: String
  ) {
    getStudentPerformanceReport(
        mobile: $mobile
      student_user_id: $student_user_id
      edu_student_id: $edu_student_id
      exam_type: $exam_type
      exam_stream: $exam_stream
      fromDate: $fromDate
      toDate: $toDate
    ) {
      metrics {
        avg_time_per_question
        attempt_rate
        rank_move_4wk
        consistency
        has_time_data
        breakdown {
          perExam {
            exam_name
            timestamp
            correct
            wrong
            attempted
            total_questions
            score
          }
          attempted_sum
          total_sum
          total_time_seconds
          total_questions_timed
          consistency_mean
          consistency_std
          consistency_cv
          rank_latest
          rank_baseline
          exams_considered
        }
      }
      rankMovement {
        label
        rank
        section_rank
        branch_rank
        city_rank
        state_rank
      }
      subjectMarksTrend {
        subject
        subject_id
        points {
          label
          exam_name
          marks
          branch_avg
          subject_max
          timestamp
        }
      }
      speedVsAccuracy {
        subject
        subject_id
        accuracy
        attempted
        avg_time_per_question
        timed_questions
        total_questions
        has_time_data
        read
      }
    }
  }
`;

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

const SUBJECT_COLORS = ["#6366f1", "#22c55e", "#a855f7", "#f59e0b", "#ef4444", "#0ea5e9"];

class PerformanceTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedExamValue: { value: 0, label: "All Exams" },
            startDateCalender: moment(new Date()).subtract(1, "month").startOf("day").unix(),
            endDateCalender: moment(new Date()).endOf("day").unix(),
            metrics: null,
            rankMovement: [],
            subjectMarksTrend: [],
            speedVsAccuracy: [],
            speedView: "quadrant", // "quadrant" | "graph" | "table" — tradeoff view first
            trendView: "graph", // "graph" | "table" — subject marks trend view
            rankView: "graph", // "graph" | "table" — rank movement view
            rankLevel: "section", // overall|section|branch|city|state — default Section
            trendLimit: 10, // how many recent exams to plot on the marks trend
            metricModal: null, // which KPI's "how it's calculated" popup is open
            loading: false,
        };
    }

    // Trend "latest N" options — keeps the chart readable and the query bounded
    // for students with many exams.
    static TREND_LIMIT_OPTIONS = [
        { value: 5, label: "Last 5" },
        { value: 10, label: "Last 10" },
        { value: 20, label: "Last 20" },
        { value: 50, label: "Last 50" },
    ];

    componentDidMount() {
        const { selectedExamValue, startDateCalender, endDateCalender } = this.state;
        this.getReport(selectedExamValue?.value, startDateCalender, endDateCalender);
    }

    // Re-fetch when the header exam-stream (Mains/Advanced) changes.
    componentDidUpdate(prevProps) {
        if (prevProps.examStream !== this.props.examStream) {
            const { selectedExamValue, startDateCalender, endDateCalender } = this.state;
            this.getReport(selectedExamValue?.value, startDateCalender, endDateCalender);
        }
    }

    selecthandleInputChange = (name, value, selectedOption) => {
        this.setState({ [name]: selectedOption }, () => {
            this.getReport(selectedOption?.value, this.state.startDateCalender, this.state.endDateCalender);
        });
    };

    applyCallback = (startDate, endDate) => {
        const start = startDate.clone().startOf("day").unix();
        const end = endDate.clone().endOf("day").unix();
        this.setState({ startDateCalender: start, endDateCalender: end }, () => {
            this.getReport(this.state.selectedExamValue?.value, start, end);
        });
    };

    // Change how many recent exams the trend plots, then re-fetch.
    setTrendLimit = (trendLimit) => {
        if (trendLimit === this.state.trendLimit) return;
        this.setState({ trendLimit }, () => {
            this.getReport(this.state.selectedExamValue?.value, this.state.startDateCalender, this.state.endDateCalender);
        });
    };

    getReport = async (examType, fromDate, toDate) => {
        const fromDateFinal = fromDate || this.state.startDateCalender;
        const toDateFinal = toDate || this.state.endDateCalender;
        const examTypeFinal = examType ?? this.state.selectedExamValue?.value;

        this.setState({ loading: true });
        try {
            const result = await this.props.client.query({
                query: GET_STUDENT_PERFORMANCE_REPORT,
                variables: {
                    mobile: `${resolveMobile(this.props.student) || ""}`,
                    student_user_id: parseInt(this.props.student?.student_user_id) || 0,
                    edu_student_id: parseInt(this.props.student?.edu_student_id) || 0,
                    exam_type: examTypeFinal,
                    exam_stream: this.props.examStream != null ? Number(this.props.examStream) : null,
                    fromDate: fromDateFinal.toString(),
                    toDate: toDateFinal.toString(),
                },
                fetchPolicy: "network-only",
            });
            const data = result?.data?.getStudentPerformanceReport;
            this.setState({
                metrics: data?.metrics || null,
                rankMovement: data?.rankMovement || [],
                subjectMarksTrend: data?.subjectMarksTrend || [],
                speedVsAccuracy: data?.speedVsAccuracy || [],
                loading: false,
            });
        } catch (error) {
            console.log(error);
            this.setState({
                metrics: null,
                rankMovement: [],
                subjectMarksTrend: [],
                speedVsAccuracy: [],
                loading: false,
            });
        }
    };

    formatValue = (value) => (value === null || value === undefined || value === "" ? "-" : value);

    // Compact Exam + Date controls rendered in the TabSection header (right side).
    renderControls = () => (
        <div className="d-flex flex-wrap align-items-center justify-content-end" style={{ gap: "12px" }}>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <span className="font-weight-bold text-muted" style={{ fontSize: "13px" }}>
                    Exam
                </span>
                <div style={{ minWidth: "170px" }}>
                    <SelectDropDown
                        stateData={this.state.selectedExamValue}
                        handleChange={this.selecthandleInputChange}
                        name="selectedExamValue"
                        options={examTypes}
                        placeholderName="Exam"
                        dropdownIndicator={{ DropdownIndicator }}
                    />
                </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <span className="font-weight-bold text-muted" style={{ fontSize: "13px" }}>
                    Date
                </span>
                <ProfileDateBlock
                    applyCallback={this.applyCallback}
                    start={moment.unix(this.state.startDateCalender)}
                    end={moment.unix(this.state.endDateCalender)}
                />
            </div>
        </div>
    );

    renderSummaryCards = (metrics) => {
        const m = metrics || {};
        const rankMove = num(m.rank_move_4wk);
        const rankImproved = rankMove <= 0; // negative/zero rank move means rank got better
        // Offline / OMR exams capture no per-question time (question_time = 0), so
        // the avg is genuinely 0 with no data behind it. Show why, instead of a
        // misleading "0.0m". has_time_data is false in that case.
        const noTimeData = m.has_time_data === false;
        const hasBreakdown = !!(m.breakdown && (m.breakdown.perExam || []).length >= 0);
        const cards = [
            {
                metricKey: "time",
                label: "Avg Time / Q",
                value: noTimeData ? "—" : (m.avg_time_per_question != null ? `${m.avg_time_per_question}m` : "-"),
                icon: "fa-stopwatch",
                iconBg: noTimeData
                    ? "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)"
                    : "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                note: noTimeData ? "No time data (offline exam)" : "speed",
                noteColor: noTimeData ? "#94a3b8" : "#16a34a",
            },
            {
                metricKey: "attempt",
                label: "Attempt Rate",
                value: m.attempt_rate != null ? `${m.attempt_rate}%` : "-",
                icon: "fa-check-circle",
                iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                note: "attempted",
                noteColor: "#16a34a",
            },
            {
                metricKey: "rank",
                label: "4-wk Rank Move",
                value:
                    m.rank_move_4wk != null
                        ? `${rankImproved ? "▼" : "▲"} ${Math.abs(rankMove)}`
                        : "-",
                icon: "fa-chart-line",
                iconBg: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                note: "within section",
                noteColor: "#4f46e5",
            },
            {
                metricKey: "consistency",
                label: "Consistency",
                value: m.consistency != null ? `${m.consistency}%` : "-",
                icon: "fa-bolt",
                iconBg: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
                note: "test-to-test",
                noteColor: "#7c3aed",
            },
        ];

        return (
            <Row className="mb-4">
                {cards.map((card) => {
                    const clickable = hasBreakdown;
                    return (
                    <Col md={3} sm={6} key={card.label} className="mb-3 mb-md-0">
                        <div
                            className="d-flex align-items-start justify-content-between p-3 h-100 sp-kpi-card"
                            onClick={clickable ? () => this.openMetricModal(card.metricKey) : undefined}
                            role={clickable ? "button" : undefined}
                            title={clickable ? "Click to see how this is calculated" : undefined}
                            style={{
                                borderRadius: "16px",
                                background: "#ffffff",
                                border: "1px solid #f1f2f5",
                                boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                                cursor: clickable ? "pointer" : "default",
                            }}
                        >
                            <div>
                                <span className="d-block text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>
                                    {card.label}
                                    {clickable ? <i className="fas fa-circle-info ml-1" style={{ fontSize: "11px", color: "#cbd5e1" }} /> : null}
                                </span>
                                <strong
                                    className="d-block text-dark"
                                    style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}
                                >
                                    {card.value}
                                </strong>
                                <span className="font-weight-bold" style={{ fontSize: "12px", color: card.noteColor }}>
                                    {card.note}
                                </span>
                            </div>
                            <div
                                className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "12px",
                                    background: card.iconBg,
                                    boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                                }}
                            >
                                <i className={`fas ${card.icon}`} style={{ fontSize: "18px" }}></i>
                            </div>
                        </div>
                    </Col>
                    );
                })}
            </Row>
        );
    };

    // Open/close the "how this KPI is calculated" popup.
    openMetricModal = (metricKey) => this.setState({ metricModal: metricKey });
    closeMetricModal = () => this.setState({ metricModal: null });

    // Card shell shared by both charts so they line up in the grid.
    renderChartCard = (title, badge, hasData, chartOptions, emptyText) => (
        <div
            className="p-3 h-100"
            style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
        >
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {title}
                </h6>
                {badge ? (
                    <span className="text-muted" style={{ fontSize: "12px" }}>
                        {badge}
                    </span>
                ) : null}
            </div>
            {hasData ? (
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                    containerProps={{ style: { width: "100%" } }}
                />
            ) : (
                <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                    {emptyText}
                </div>
            )}
        </div>
    );

    // Rank Movement Over Time — per exam, at the selected level (overall/section/
    // branch/city/state). Lower rank is better → reversed axis. Per-point arrows
    // show improve/decline vs the previous exam.
    renderRankMovementChart = (rankMovement) => {
        const level = this.state.rankLevel || "section";
        const FIELD = { overall: "rank", section: "section_rank", branch: "branch_rank", city: "city_rank", state: "state_rank" }[level];
        const levelLabel = { overall: "Overall", section: "Section", branch: "Branch", city: "City", state: "State" }[level];
        const categories = rankMovement.map((r) => r.label || "-");
        // Each point carries the previous rank so the label can show ▼/▲ movement.
        const data = rankMovement.map((r, i) => {
            const y = r[FIELD] != null ? num(r[FIELD]) : null;
            const prev = i > 0 && rankMovement[i - 1][FIELD] != null ? num(rankMovement[i - 1][FIELD]) : null;
            return { y, prevRank: prev };
        });
        const options = {
            chart: { type: "areaspline", backgroundColor: "transparent", height: 300 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: {
                reversed: true,
                title: { text: "Rank (lower is better ↑)", style: { color: "#94a3b8" } },
                gridLineColor: "#eef2f6",
                labels: { style: { color: "#64748b" } },
            },
            tooltip: pointTooltip({ valueLabel: `${levelLabel} Rank` }),
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: {
                areaspline: {
                    marker: { enabled: true, radius: 4 },
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        // ▼ green = improved (rank dropped), ▲ red = declined vs prev exam.
                        formatter: function () {
                            const prev = this.point.prevRank;
                            if (prev == null) return `<span style="font-weight:700;color:#334155">${this.y}</span>`;
                            const d = prev - this.y;
                            if (d === 0) return `<span style="font-weight:700;color:#334155">${this.y}</span>`;
                            const up = d > 0;
                            return `<span style="font-weight:700;color:#334155">${this.y}</span> <span style="font-size:10px;font-weight:700;color:${up ? "#16a34a" : "#dc2626"}">${up ? "▼" : "▲"}${Math.abs(Math.round(d))}</span>`;
                        },
                    },
                },
            },
            series: [
                {
                    name: `${levelLabel} Rank`,
                    data,
                    color: "#0ea5e9",
                    lineWidth: 3,
                    fillColor: "rgba(14, 165, 233, 0.12)",
                },
            ],
        };
        const hasData = rankMovement.length > 0;
        const view = this.state.rankView;
        const LEVELS = [
            { key: "overall", label: "Overall" },
            { key: "section", label: "Section" },
            { key: "branch", label: "Branch" },
            { key: "city", label: "City" },
            { key: "state", label: "State" },
        ];
        return (
            <div
                className="p-3 h-100"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                <div className="d-flex align-items-start justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
                    <div>
                        <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            Rank Movement — {levelLabel}
                        </h6>
                        {hasData ? (
                            <div className="sp-segment sp-segment-sm">
                                {LEVELS.map((l) => (
                                    <button key={l.key} type="button" className={level === l.key ? "active" : ""} onClick={() => this.setState({ rankLevel: l.key })}>
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    {hasData ? (
                        <div className="sp-segment sp-segment-sm">
                            <button type="button" className={view === "graph" ? "active" : ""} onClick={() => this.setState({ rankView: "graph" })}>
                                <i className="fas fa-chart-column" />Graph
                            </button>
                            <button type="button" className={view === "table" ? "active" : ""} onClick={() => this.setState({ rankView: "table" })}>
                                <i className="fas fa-table" />Table
                            </button>
                        </div>
                    ) : null}
                </div>
                {!hasData ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No rank movement available.
                    </div>
                ) : view === "table" ? (
                    <div className="table-responsive">
                        <table className="table table-borderless mb-0">
                            <thead>
                                <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                    <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Exam</th>
                                    <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>{levelLabel} Rank</th>
                                    <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Change</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rankMovement.map((r, i) => {
                                    const cur = r[FIELD] != null ? num(r[FIELD]) : null;
                                    const prev = i > 0 && rankMovement[i - 1][FIELD] != null ? num(rankMovement[i - 1][FIELD]) : null;
                                    let move = null;
                                    if (prev != null && cur != null && prev !== cur) {
                                        const up = prev - cur > 0;
                                        move = { up, amt: Math.abs(Math.round(prev - cur)) };
                                    }
                                    return (
                                        <tr key={i} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                            <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{r.label || "-"}</td>
                                            <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{cur != null ? cur : "-"}</td>
                                            <td className="text-right" style={{ fontSize: 13 }}>
                                                {move ? (
                                                    <span style={{ fontWeight: 700, color: move.up ? "#16a34a" : "#dc2626" }}>{move.up ? "▼" : "▲"} {move.amt}</span>
                                                ) : <span className="text-muted">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                )}
            </div>
        );
    };

    // Subject-wise Marks Trend — one spline per subject across exams, each paired
    // with a dashed "branch average" line for that subject (student vs batch).
    // The student-vs-branch delta is shown per-exam on hover (custom tooltip),
    // not as a static chip. Includes a "latest N" selector.
    renderSubjectMarksChart = (subjectMarksTrend) => {
        // Use the longest point list to drive the x-axis labels.
        const longest = subjectMarksTrend.reduce(
            (acc, s) => ((s.points || []).length > acc.length ? s.points : acc),
            []
        );
        const categories = longest.map((p) => p.exam_name || "-");
        const hasData = subjectMarksTrend.some((s) => (s.points || []).length > 0);
        // Does any point carry a branch average? Only add the comparison lines if so.
        const hasBranchAvg = subjectMarksTrend.some((s) =>
            (s.points || []).some((p) => p.branch_avg != null)
        );
        // Dynamic y-axis max = the largest per-subject max marks across all points
        // (e.g. 100 for a 25-question, +4 subject). Falls back to auto when unknown.
        let subjectMaxMarks = 0;
        subjectMarksTrend.forEach((s) =>
            (s.points || []).forEach((p) => {
                if (p.subject_max != null && num(p.subject_max) > subjectMaxMarks) subjectMaxMarks = num(p.subject_max);
            })
        );

        const series = [];
        subjectMarksTrend.forEach((s, i) => {
            const color = SUBJECT_COLORS[i % SUBJECT_COLORS.length];
            const subjectName = s.subject || `Subject ${s.subject_id}`;
            // Student's own marks (solid, prominent line). Each point carries its
            // branch average so the hover tooltip can show the per-exam delta.
            series.push({
                id: `subj-${s.subject_id}`,
                name: subjectName,
                data: (s.points || []).map((p) => ({
                    y: num(p.marks),
                    branchAvg: p.branch_avg != null ? num(p.branch_avg) : null,
                })),
                color,
                lineWidth: 3,
                zIndex: 2,
                marker: { enabled: true, radius: 4, symbol: "circle", lineWidth: 2, lineColor: "#fff" },
            });
            // Branch average for the same subject (dashed, dimmed, same color).
            if (hasBranchAvg) {
                series.push({
                    linkedTo: `subj-${s.subject_id}`,
                    name: `${subjectName} — Branch Avg`,
                    data: (s.points || []).map((p) => (p.branch_avg != null ? num(p.branch_avg) : null)),
                    color,
                    dashStyle: "ShortDash",
                    lineWidth: 1.5,
                    opacity: 0.5,
                    zIndex: 1,
                    marker: { enabled: true, radius: 2.5, symbol: "diamond" },
                    dataLabels: { enabled: false },
                    enableMouseTracking: true,
                });
            }
        });

        const options = {
            chart: { type: "spline", backgroundColor: "transparent", height: 320, spacingTop: 6 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: "#e2e8f0",
                tickLength: 0,
                labels: { style: { color: "#64748b", fontWeight: "600", fontSize: "11px" }, rotation: categories.length > 4 ? -35 : 0 },
            },
            yAxis: {
                min: 0,
                // Scale to the real per-subject max marks (e.g. 0–100), so a score
                // reads as "X out of max". Stops exactly at the max AND labels it.
                max: subjectMaxMarks > 0 ? subjectMaxMarks : null,
                endOnTick: false,
                startOnTick: true,
                maxPadding: 0,
                showLastLabel: true,
                tickPositioner: subjectMaxMarks > 0
                    ? function () {
                        const step = Math.ceil(subjectMaxMarks / 4);
                        const ticks = [];
                        for (let v = 0; v < subjectMaxMarks; v += step) ticks.push(v);
                        ticks.push(subjectMaxMarks);
                        return ticks;
                    }
                    : undefined,
                title: {
                    text: subjectMaxMarks > 0 ? `Marks (out of ${subjectMaxMarks})` : "Marks",
                    style: { color: "#94a3b8", fontSize: "11px" },
                },
                gridLineColor: "#eef2f6",
                labels: { style: { color: "#64748b" } },
            },
            // Per-exam student-vs-branch comparison shown on hover only. Hovering a
            // student line point renders its marks, the branch average and the
            // individual delta (green if ahead of branch, red if behind).
            tooltip: {
                enabled: true,
                useHTML: true,
                shared: false,
                backgroundColor: "#fff",
                borderColor: "#e2e8f0",
                borderRadius: 10,
                shadow: true,
                formatter: function () {
                    const val = Math.round(this.y);
                    // The dashed comparison series is the branch line (it has no
                    // per-point branchAvg and its name ends with "Branch Avg").
                    const isBranchLine =
                        (this.series && /Branch Avg$/.test(this.series.name)) ||
                        !(this.point && this.point.branchAvg != null);

                    if (isBranchLine) {
                        // Hovering the branch line: this value IS the branch average.
                        const subject = (this.series.name || "").replace(/ — Branch Avg$/, "");
                        return (
                            `<div style="min-width:140px;padding:2px 4px">` +
                            `<div style="font-size:11px;color:#94a3b8;margin-bottom:2px">${this.key || ""}</div>` +
                            `<div style="font-weight:700;font-size:13px;color:${this.series.color}">${subject}</div>` +
                            `<div style="font-size:12px;color:#334155">Branch average: <b>${val}</b></div>` +
                            `</div>`
                        );
                    }

                    // Hovering the student line: show marks, branch avg, and delta.
                    const ba = this.point && this.point.branchAvg != null ? Math.round(this.point.branchAvg) : null;
                    let delta = "";
                    if (ba != null) {
                        const d = val - ba;
                        const up = d >= 0;
                        delta =
                            `<div style="margin-top:6px;font-weight:700;font-size:12px;color:${up ? "#16a34a" : "#dc2626"}">` +
                            `${up ? "▲" : "▼"} ${up ? "+" : ""}${d} vs branch</div>`;
                    }
                    const branchRow = ba != null
                        ? `<div style="font-size:11.5px;color:#94a3b8">Branch avg: <b style="color:#64748b">${ba}</b></div>`
                        : "";
                    return (
                        `<div style="min-width:140px;padding:2px 4px">` +
                        `<div style="font-size:11px;color:#94a3b8;margin-bottom:2px">${this.key || ""}</div>` +
                        `<div style="font-weight:700;font-size:13px;color:${this.series.color}">${this.series.name}</div>` +
                        `<div style="font-size:12px;color:#334155">Marks: <b>${val}</b></div>` +
                        branchRow + delta +
                        `</div>`
                    );
                },
            },
            legend: { align: "center", verticalAlign: "top", itemStyle: { fontSize: "12px", fontWeight: "600" } },
            plotOptions: {
                spline: {
                    dataLabels: { enabled: true, style: { fontWeight: "700", fontSize: "10px", color: "#334155", textOutline: "2px #fff" } },
                    states: { hover: { lineWidthPlus: 1 } },
                },
            },
            series,
        };

        return (
            <div className="p-3 h-100" style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}>
                <div className="d-flex align-items-start justify-content-between flex-wrap mb-2" style={{ gap: 10 }}>
                    <div>
                        <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            Subject-wise Marks Trend
                        </h6>
                        <span className="text-muted" style={{ fontSize: "11.5px" }}>
                            Solid = student &nbsp;·&nbsp; dashed = branch average
                        </span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                        {/* Latest-N selector — most recent N exams (not days); keeps
                            the chart readable when a student has many exams. */}
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <span className="text-muted font-weight-bold" style={{ fontSize: "11.5px", whiteSpace: "nowrap" }}>
                                Recent exams:
                            </span>
                            <div className="sp-segment sp-segment-sm">
                                {PerformanceTab.TREND_LIMIT_OPTIONS.map((o) => (
                                    <button
                                        key={o.value}
                                        type="button"
                                        className={this.state.trendLimit === o.value ? "active" : ""}
                                        onClick={() => this.setTrendLimit(o.value)}
                                    >
                                        {o.value}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {hasData ? (
                            <div className="sp-segment sp-segment-sm">
                                <button type="button" className={this.state.trendView === "graph" ? "active" : ""} onClick={() => this.setState({ trendView: "graph" })}>
                                    <i className="fas fa-chart-column" />Graph
                                </button>
                                <button type="button" className={this.state.trendView === "table" ? "active" : ""} onClick={() => this.setState({ trendView: "table" })}>
                                    <i className="fas fa-table" />Table
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>

                {!hasData ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No subject marks trend available.
                    </div>
                ) : this.state.trendView === "table" ? (
                    <div className="table-responsive">
                        {subjectMarksTrend.map((s, si) => {
                            const points = s.points || [];
                            if (points.length === 0) return null;
                            const subjectName = s.subject || `Subject ${s.subject_id}`;
                            const showBranch = points.some((p) => p.branch_avg != null);
                            return (
                                <div key={s.subject_id ?? si} className={si > 0 ? "mt-3" : ""}>
                                    <div className="font-weight-bold text-dark mb-1" style={{ fontSize: 13 }}>{subjectName}</div>
                                    <table className="table table-borderless mb-0">
                                        <thead>
                                            <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                                <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Exam</th>
                                                <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Marks</th>
                                                {showBranch && <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Branch Avg</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {points.map((p, pi) => (
                                                <tr key={pi} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                                    <td className="font-weight-bold text-dark" style={{ fontSize: 13 }}>{p.exam_name || "-"}</td>
                                                    <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{p.marks != null ? p.marks : "-"}</td>
                                                    {showBranch && <td className="text-right text-muted" style={{ fontSize: 13 }}>{p.branch_avg != null ? p.branch_avg : "-"}</td>}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />
                )}
            </div>
        );
    };

    // "READ" badge styling mirrors the design — colored chip based on the verdict.
    renderReadBadge = (read) => {
        const text = this.formatValue(read);
        if (text === "-") return <span className="text-muted">-</span>;
        const lower = String(read).toLowerCase();
        let bg = "#ecfdf5";
        let color = "#16a34a";
        if (lower.includes("slow")) {
            bg = "#ecfdf5";
            color = "#16a34a";
        } else if (lower.includes("fast") && lower.includes("inaccurate")) {
            bg = "#fef2f2";
            color = "#dc2626";
        } else if (lower.includes("inaccurate") || lower.includes("careless")) {
            bg = "#fffbeb";
            color = "#d97706";
        }
        return (
            <span
                className="font-weight-bold"
                style={{
                    background: bg,
                    color,
                    borderRadius: "8px",
                    padding: "4px 10px",
                    fontSize: "12px",
                }}
            >
                {text}
            </span>
        );
    };

    // Speed vs Accuracy — clear dual-axis chart per subject:
    // columns = Accuracy % / Attempted % (left axis), line = Avg Time/Q min (right axis).
    renderSpeedGraph = (rows) => {
        const categories = rows.map((r) => this.formatValue(r.subject));
        const options = {
            chart: { backgroundColor: "transparent", height: 340 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                categories,
                crosshair: true,
                lineColor: "#e2e8f0",
                labels: { style: { color: "#64748b", fontWeight: "600" } },
            },
            yAxis: [
                {
                    title: { text: "Percentage (%)", style: { color: "#94a3b8" } },
                    min: 0,
                    max: 100,
                    gridLineColor: "#eef2f6",
                    labels: { format: "{value}%", style: { color: "#64748b" } },
                },
                {
                    title: { text: "Avg Time / Q (min)", style: { color: "#94a3b8" } },
                    min: 0,
                    opposite: true,
                    gridLineWidth: 0,
                    labels: { format: "{value}m", style: { color: "#f59e0b" } },
                },
            ],
            tooltip: pointTooltip({ unitBySeries: { "Accuracy": "%", "Attempted": "%", "Avg Time/Q": "m" } }),
            legend: { align: "center", verticalAlign: "top" },
            plotOptions: {
                column: { borderRadius: 4, groupPadding: 0.14, dataLabels: { enabled: true, style: { fontWeight: "700", fontSize: "10.5px", textOutline: "none" } } },
                spline: { dataLabels: { enabled: true, style: { fontWeight: "700", color: "#334155" } } },
            },
            series: [
                { name: "Accuracy", type: "column", yAxis: 0, color: "#22c55e", data: rows.map((r) => num(r.accuracy)), tooltip: { valueSuffix: "%" } },
                { name: "Attempted", type: "column", yAxis: 0, color: "#6366f1", data: rows.map((r) => num(r.attempted)), tooltip: { valueSuffix: "%" } },
                {
                    name: "Avg Time/Q",
                    type: "spline",
                    yAxis: 1,
                    color: "#f59e0b",
                    lineWidth: 3,
                    marker: { enabled: true, radius: 5 },
                    data: rows.map((r) => num(r.avg_time_per_question)),
                    tooltip: { valueSuffix: "m" },
                },
            ],
        };
        return <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />;
    };

    // Speed-vs-Accuracy QUADRANT — the core tradeoff view. x = avg time/Q (min),
    // y = accuracy %. Each subject is a bubble; the four quadrants read as
    // Fast&Accurate (ideal), Slow&Accurate, Fast&Inaccurate (careless), Slow&
    // Inaccurate. Only subjects WITH time data are plotted.
    renderSpeedQuadrant = (rows) => {
        const timed = rows.filter((r) => r.has_time_data && r.avg_time_per_question != null);
        if (!timed.length) return null;
        const SUBJ_COLORS = ["#6366f1", "#22c55e", "#a855f7", "#f59e0b", "#ef4444", "#0ea5e9"];
        // Axis bounds: accuracy 0–100; time 0 → a bit beyond the slowest subject.
        const maxTime = Math.max(...timed.map((r) => num(r.avg_time_per_question)), 2);
        const timeAxisMax = Math.ceil(maxTime + 0.5);
        // Midlines: accuracy 80 (accurate threshold), time 2 min (slow threshold).
        const options = {
            chart: { type: "scatter", backgroundColor: "transparent", height: 360 },
            title: { text: "" },
            credits: { enabled: false },
            xAxis: {
                min: 0, max: timeAxisMax,
                title: { text: "Avg time per question (min)", style: { color: "#94a3b8", fontSize: "11px" } },
                gridLineColor: "#eef2f6",
                plotLines: [{ value: 2, color: "#f59e0b", dashStyle: "Dash", width: 1, label: { text: "slow →", style: { color: "#f59e0b", fontSize: "10px" } } }],
                labels: { format: "{value}m", style: { color: "#64748b" } },
            },
            yAxis: {
                min: 0, max: 100,
                title: { text: "Accuracy %", style: { color: "#94a3b8", fontSize: "11px" } },
                gridLineColor: "#eef2f6",
                plotLines: [{ value: 80, color: "#22c55e", dashStyle: "Dash", width: 1, label: { text: "accurate ↑", style: { color: "#22c55e", fontSize: "10px" } } }],
                labels: { format: "{value}%", style: { color: "#64748b" } },
            },
            legend: { align: "center", verticalAlign: "top" },
            tooltip: {
                useHTML: true,
                formatter: function () {
                    return `<b style="color:${this.point.color}">${this.point.name}</b><br/>Accuracy: <b>${this.y}%</b><br/>Avg time/Q: <b>${this.x}m</b><br/><span style="font-size:11px;color:#64748b">${this.point.readLabel}</span>`;
                },
            },
            plotOptions: {
                scatter: {
                    marker: { radius: 9, symbol: "circle", lineWidth: 2, lineColor: "#fff" },
                    dataLabels: { enabled: true, format: "{point.name}", style: { fontWeight: "700", fontSize: "11px", color: "#334155", textOutline: "2px #fff" } },
                },
            },
            series: timed.map((r, i) => ({
                name: this.formatValue(r.subject),
                color: SUBJ_COLORS[i % SUBJ_COLORS.length],
                data: [{
                    name: this.formatValue(r.subject),
                    x: num(r.avg_time_per_question),
                    y: num(r.accuracy),
                    color: SUBJ_COLORS[i % SUBJ_COLORS.length],
                    readLabel: r.read || "",
                }],
            })),
        };
        return <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: "100%" } }} />;
    };

    // Speed vs Accuracy — per subject, switchable between table, graph and quadrant.
    renderSpeedVsAccuracy = (rows) => {
        const view = this.state.speedView;
        return (
            <div
                className="p-3 mt-4"
                style={{ borderRadius: "16px", border: "1px solid #f1f2f5", background: "#ffffff" }}
            >
                {/* Coverage transparency: how many questions actually had timing. */}
                {(() => {
                    const totTimed = rows.reduce((a, r) => a + (Number(r.timed_questions) || 0), 0);
                    const totAll = rows.reduce((a, r) => a + (Number(r.total_questions) || 0), 0);
                    const anyTime = rows.some((r) => r.has_time_data);
                    return (
                        <div className="d-flex align-items-start justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
                            <div>
                                <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                    Speed vs Accuracy — per subject
                                </h6>
                                <span className="text-muted" style={{ fontSize: "11.5px" }}>
                                    {anyTime
                                        ? `Based on ${totTimed} of ${totAll} timed questions`
                                        : "No per-question timing recorded for these exams (offline / OMR)"}
                                </span>
                            </div>
                            {anyTime ? (
                                <div className="sp-segment sp-segment-sm">
                                    <button type="button" className={view === "quadrant" ? "active" : ""} onClick={() => this.setState({ speedView: "quadrant" })}>
                                        <i className="fas fa-braille" />Quadrant
                                    </button>
                                    <button type="button" className={view === "graph" ? "active" : ""} onClick={() => this.setState({ speedView: "graph" })}>
                                        <i className="fas fa-chart-column" />Bars
                                    </button>
                                    <button type="button" className={view === "table" ? "active" : ""} onClick={() => this.setState({ speedView: "table" })}>
                                        <i className="fas fa-table" />Table
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    );
                })()}
                {rows.length === 0 ? (
                    <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                        No speed vs accuracy data available.
                    </div>
                ) : !rows.some((r) => r.has_time_data) ? (
                    // Honest empty-state: offline exams have no time-spent data.
                    <div className="text-center py-4">
                        <i className="far fa-clock" style={{ fontSize: 28, color: "#cbd5e1" }} />
                        <p className="text-muted mb-1 mt-2" style={{ fontSize: 14, fontWeight: 600 }}>
                            Time-per-question isn't available for these exams
                        </p>
                        <p className="text-muted mb-0" style={{ fontSize: 12.5 }}>
                            These were offline / OMR tests, which don't record how long each question took.
                            Speed-vs-accuracy appears automatically for online / timed exams.
                        </p>
                    </div>
                ) : view === "quadrant" ? (
                    this.renderSpeedQuadrant(rows)
                ) : view === "graph" ? (
                    this.renderSpeedGraph(rows.filter((r) => r.has_time_data))
                ) : (
                    <div className="table-responsive">
                        <table className="table table-borderless mb-0">
                            <thead>
                                <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                    <th className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase" }}>Subject</th>
                                    <th className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase" }}>Accuracy</th>
                                    <th className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase" }}>Avg Time/Q</th>
                                    <th className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase" }}>Timed Q</th>
                                    <th className="text-muted" style={{ fontSize: "12px", textTransform: "uppercase" }}>Read</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.subject_id ?? r.subject} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                        <td className="font-weight-bold text-dark">{this.formatValue(r.subject)}</td>
                                        <td>{r.accuracy != null ? `${r.accuracy}%` : "-"}</td>
                                        <td>{r.avg_time_per_question != null ? `${r.avg_time_per_question}m` : <span className="text-muted">—</span>}</td>
                                        <td className="text-muted">{r.timed_questions != null ? `${r.timed_questions}/${r.total_questions}` : "-"}</td>
                                        <td>{this.renderReadBadge(r.read)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    // Small formatted date from a unix timestamp string.
    fmtTs = (ts) => {
        if (!ts) return "";
        const n = Number(ts);
        const m = n < 1000000000000 ? moment.unix(n) : moment(n);
        return m.isValid() ? m.format("DD MMM YYYY") : "";
    };

    // KPI detail popup — a clean, plain-English breakdown of the exams behind the
    // number. No formulas: just the value, a short caption, and a neat table.
    renderMetricModal = (metrics) => {
        const which = this.state.metricModal;
        if (!which) return null;
        const m = metrics || {};
        const b = m.breakdown || {};
        const rows = b.perExam || [];
        const examCount = b.exams_considered != null ? b.exams_considered : rows.length;

        // Per-metric heading value, caption and table columns.
        const META = {
            attempt: {
                title: "Attempt Rate",
                value: m.attempt_rate != null ? `${m.attempt_rate}%` : "—",
                caption: `Questions attempted across ${examCount} exam${examCount === 1 ? "" : "s"}.`,
                accent: "#16a34a",
            },
            time: {
                title: "Avg Time / Question",
                value: m.has_time_data === false ? "—" : (m.avg_time_per_question != null ? `${m.avg_time_per_question}m` : "—"),
                caption: m.has_time_data === false
                    ? "These are offline exams — per-question timing isn't recorded."
                    : `Average time per question across ${examCount} exam${examCount === 1 ? "" : "s"}.`,
                accent: "#f59e0b",
            },
            rank: {
                title: "4-Week Section Rank Move",
                value: m.rank_move_4wk != null ? `${m.rank_move_4wk <= 0 ? "▼" : "▲"} ${Math.abs(num(m.rank_move_4wk))}` : "—",
                caption: b.rank_baseline != null
                    ? `Rank within section moved from ${b.rank_baseline} to ${b.rank_latest} over the last 4 weeks.`
                    : "Not enough ranked exams yet to show a movement.",
                accent: "#4f46e5",
            },
            consistency: {
                title: "Consistency",
                value: m.consistency != null ? `${m.consistency}%` : "—",
                caption: examCount <= 1
                    ? "Only one exam in range — consistency shows once there are more."
                    : `How steady the scores are across ${examCount} exams. Higher = steadier.`,
                accent: "#7c3aed",
            },
        };
        const meta = META[which] || {};

        // Column set per metric — attempt shows the answer split; the rest show score.
        const showAttemptCols = which === "attempt";
        const showScoreCol = which === "consistency" || which === "rank";

        return (
            <Modal show={!!which} onHide={this.closeMetricModal} centered>
                <Modal.Header closeButton style={{ borderBottom: "1px solid #f1f2f5" }}>
                    <Modal.Title style={{ fontSize: 16, fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
                        {meta.title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ paddingTop: 18 }}>
                    {/* Headline value + one-line caption */}
                    <div className="d-flex align-items-baseline mb-1" style={{ gap: 10 }}>
                        <span style={{ fontSize: "2rem", fontWeight: 700, fontFamily: "'Outfit', sans-serif", color: meta.accent, lineHeight: 1 }}>
                            {meta.value}
                        </span>
                    </div>
                    <p className="text-muted mb-3" style={{ fontSize: 13 }}>{meta.caption}</p>

                    {/* Neat per-exam breakdown */}
                    {rows.length ? (
                        <div className="table-responsive">
                            <table className="table table-borderless mb-0">
                                <thead>
                                    <tr style={{ borderBottom: "1px solid #eef2f6" }}>
                                        <th className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Exam</th>
                                        {showAttemptCols && <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Attempted</th>}
                                        {showAttemptCols && <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Total</th>}
                                        {showScoreCol && <th className="text-muted text-right" style={{ fontSize: 11, textTransform: "uppercase", fontWeight: 700 }}>Score</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i} style={{ borderBottom: "1px solid #f6f8fb" }}>
                                            <td style={{ fontSize: 13 }}>
                                                <span className="font-weight-bold text-dark">{r.exam_name || "Exam"}</span>
                                                <div className="text-muted" style={{ fontSize: 11 }}>{this.fmtTs(r.timestamp)}</div>
                                            </td>
                                            {showAttemptCols && <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{r.attempted}</td>}
                                            {showAttemptCols && <td className="text-right text-muted" style={{ fontSize: 13 }}>{r.total_questions}</td>}
                                            {showScoreCol && <td className="text-right font-weight-bold" style={{ fontSize: 13 }}>{r.score}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>
                            No exam data in this range.
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        );
    };

    render() {
        const { metrics, rankMovement, subjectMarksTrend, speedVsAccuracy, loading } = this.state;

        return (
            <TabSection
                icon="fa-chart-line"
                title="Performance"
                description="Exam-wise and subject-wise performance trends."
                headerRight={this.renderControls()}
            >
                {loading ? (
                    <PageShimmer />
                ) : (
                    <Row>
                        <Col sm={12}>{this.renderSummaryCards(metrics)}</Col>

                        <Col md={6} className="mb-4 mb-md-0" style={{ minWidth: 0 }}>
                            {this.renderSubjectMarksChart(subjectMarksTrend)}
                        </Col>

                        <Col md={6} style={{ minWidth: 0 }}>
                            {this.renderRankMovementChart(rankMovement)}
                        </Col>

                        <Col sm={12}>{this.renderSpeedVsAccuracy(speedVsAccuracy)}</Col>
                    </Row>
                )}
                {this.renderMetricModal(metrics)}
            </TabSection>
        );
    }
}

export default compose(withApollo, withRouter)(PerformanceTab);

